import { useEffect, useState } from 'react';

import { useSnackbar } from 'src/components/snackbar';
import { db } from 'src/utils/firebase';

/**
 * Custom hook for managing notifications in Firestore.
 * @param {string} userId - The user ID to fetch notifications for.
 * @returns {object} An object containing notifications array and CRUD operations.
 */
const useNotificationService = (userId) => {
  // State to hold notifications
  const [notifications, setNotifications] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  // Effect to subscribe to changes in the notifications collection
  useEffect(() => {
    // Subscribe to changes in the notifications collection for the current user
    const unsubscribe = db
      .collection(`users/${userId}/notifications`)
      .orderBy('timestamp', 'desc')
      .onSnapshot((snapshot) => {
        // Extract notification data from snapshot and update state
        const notificationsData = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));

        if (loaded) {
          const newNotifications = notificationsData
            .filter(({ id }) => !notifications.includes((e) => e.id === id))
            .filter((e) => e.read !== true);

          newNotifications.forEach((notification) => {
            enqueueSnackbar(notification.title, { variant: 'info' });
          });
        }

        setNotifications(notificationsData);
        setLoaded(true);
      });

    // Unsubscribe from snapshot listener when component unmounts
    return () => unsubscribe();
  }, [userId]); // Re-run effect when userId changes

  const notificationsRef = db.collection('users').doc(userId).collection('notifications');

  // Function to mark a notification as read
  const markNotificationAsRead = async (notification) => {
    await db
      .collection('users')
      .doc(userId)
      .collection('notifications')
      .doc(notification.id)
      .update({ read: true });
  };

  // Function to clear a notification
  const clearNotifications = async () => {
    console.log('Clearing notifications...');
    const batch = db.batch();

    const unread = notifications.filter((e) => e.read !== true);
    unread.forEach(async (notification) => {
      const ref = notificationsRef.doc(notification.id);
      batch.update(ref, { read: true });
    });

    await batch.commit();
  };

  // Return notifications array and CRUD operations
  return { notifications, markNotificationAsRead, clearNotifications };
};

export default useNotificationService;
